/**
 * Points Redemption — Shopify frontend logic.
 *
 * States: loading → verify (phone → otp) → dashboard
 * All API calls go to the Points API backend.
 * Persistent linking: once verified, JWT is refreshed on each visit.
 */

(function () {
  'use strict';

  // ── Config ──────────────────────────────────────────────────────────────
  const app = document.getElementById('points-app');
  if (!app) return;

  const API_BASE = (app.dataset.apiBase || '').replace(/\/$/, '');
  const CUSTOMER_ID = app.dataset.customerId || '';
  const CUSTOMER_EMAIL = app.dataset.customerEmail || '';

  if (!CUSTOMER_ID) return; // Not logged in, server-rendered login gate

  // ── DOM refs ────────────────────────────────────────────────────────────
  const elLoading = document.getElementById('points-loading');
  const elVerify = document.getElementById('points-verify');
  const elDashboard = document.getElementById('points-dashboard');
  const elPhoneStep = document.getElementById('verify-phone-step');
  const elOtpStep = document.getElementById('verify-otp-step');
  const elMsgVerify = document.getElementById('points-msg-verify');
  const elMsgDashboard = document.getElementById('points-msg-dashboard');

  // Inputs
  const inputPhone = document.getElementById('points-phone');
  const inputOtp = document.getElementById('points-otp');
  const inputAmount = document.getElementById('redeem-amount');

  // Buttons
  const btnSendOtp = document.getElementById('btn-send-otp');
  const btnVerifyOtp = document.getElementById('btn-verify-otp');
  const btnResendOtp = document.getElementById('btn-resend-otp');
  const btnRedeem = document.getElementById('btn-redeem');
  const btnCopyCode = document.getElementById('btn-copy-code');
  const btnNewCode = document.getElementById('btn-new-code');

  // Display
  const elOtpMaskedPhone = document.getElementById('otp-masked-phone');
  const elResendCountdown = document.getElementById('resend-countdown');
  const elBalanceAmount = document.getElementById('balance-amount');
  const elBalanceDetail = document.getElementById('balance-detail');
  const elVoucherTypes = document.getElementById('voucher-types');
  const elAmountPreview = document.getElementById('amount-preview');
  const elVoucherResult = document.getElementById('voucher-result');
  const elRedeemSection = document.getElementById('redeem-section');
  const elResultCode = document.getElementById('result-code');
  const elResultMeta = document.getElementById('result-meta');
  const elVoucherList = document.getElementById('voucher-list');
  const elVoucherEmpty = document.getElementById('voucher-empty');

  // ── State ───────────────────────────────────────────────────────────────
  let token = null;
  let balance = null;
  let voucherTypes = [];
  let selectedType = null;
  let phoneNumber = '';
  let resendTimer = null;

  // ── Helpers ─────────────────────────────────────────────────────────────

  async function api(method, path, body) {
    const opts = {
      method,
      headers: { 'Content-Type': 'application/json' },
    };
    if (token) opts.headers['Authorization'] = 'Bearer ' + token;
    if (body) opts.body = JSON.stringify(body);

    const resp = await fetch(API_BASE + path, opts);
    const data = await resp.json();
    if (!resp.ok) {
      throw new Error(data.detail || 'Bir hata oluştu.');
    }
    return data;
  }

  function show(el) { if (el) el.style.display = ''; }
  function hide(el) { if (el) el.style.display = 'none'; }

  function showMsg(container, text, type) {
    if (!container) return;
    container.innerHTML =
      '<div class="points__msg points__msg--' + type + '">' +
      escapeHtml(text) + '</div>';
  }

  function clearMsg(container) {
    if (container) container.innerHTML = '';
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function setLoading(btn, loading) {
    if (!btn) return;
    btn.disabled = loading;
    if (loading) {
      btn.dataset.originalText = btn.textContent;
      btn.innerHTML = '<span class="points__spinner"></span> Lütfen bekleyin...';
    } else {
      btn.textContent = btn.dataset.originalText || btn.textContent;
    }
  }

  function formatNumber(n) {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  }

  // ── Init ────────────────────────────────────────────────────────────────

  async function init() {
    try {
      // Try to refresh token (already linked?)
      const resp = await api('POST', '/api/points/refresh-token?shopify_customer_id=' + CUSTOMER_ID);
      token = resp.token;
      // Already verified — go to dashboard
      hide(elLoading);
      show(elDashboard);
      await loadDashboard();
    } catch (e) {
      // Not linked yet — show verification
      hide(elLoading);
      show(elVerify);
    }
  }

  // ── Phone verification ──────────────────────────────────────────────────

  btnSendOtp.addEventListener('click', async function () {
    clearMsg(elMsgVerify);
    phoneNumber = (inputPhone.value || '').trim();
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 10) {
      showMsg(elMsgVerify, 'Lütfen geçerli bir telefon numarası girin.', 'error');
      return;
    }

    setLoading(btnSendOtp, true);
    try {
      const resp = await api('POST', '/api/points/identify', {
        phone_number: phoneNumber,
        shopify_customer_id: CUSTOMER_ID,
        shopify_email: CUSTOMER_EMAIL,
      });

      if (resp.already_linked) {
        // Already linked — get token and go to dashboard
        const tokenResp = await api('POST', '/api/points/refresh-token?shopify_customer_id=' + CUSTOMER_ID);
        token = tokenResp.token;
        hide(elVerify);
        show(elDashboard);
        await loadDashboard();
        return;
      }

      // Show OTP step
      elOtpMaskedPhone.textContent = resp.masked_phone;
      hide(elPhoneStep);
      show(elOtpStep);
      inputOtp.focus();
      startResendCountdown();

    } catch (e) {
      showMsg(elMsgVerify, e.message, 'error');
    } finally {
      setLoading(btnSendOtp, false);
    }
  });

  btnVerifyOtp.addEventListener('click', async function () {
    clearMsg(elMsgVerify);
    const code = (inputOtp.value || '').trim();
    if (!code || code.length < 4) {
      showMsg(elMsgVerify, 'Lütfen doğrulama kodunu girin.', 'error');
      return;
    }

    setLoading(btnVerifyOtp, true);
    try {
      const resp = await api('POST', '/api/points/verify', {
        phone_number: phoneNumber,
        otp_code: code,
        shopify_customer_id: CUSTOMER_ID,
        shopify_email: CUSTOMER_EMAIL,
      });

      token = resp.token;
      hide(elVerify);
      show(elDashboard);
      await loadDashboard();

    } catch (e) {
      showMsg(elMsgVerify, e.message, 'error');
    } finally {
      setLoading(btnVerifyOtp, false);
    }
  });

  // OTP input: auto-submit on 6 digits
  inputOtp.addEventListener('input', function () {
    this.value = this.value.replace(/\D/g, '');
    if (this.value.length === 6) {
      btnVerifyOtp.click();
    }
  });

  // Resend countdown
  function startResendCountdown() {
    let seconds = 60;
    btnResendOtp.disabled = true;
    elResendCountdown.textContent = seconds;

    if (resendTimer) clearInterval(resendTimer);
    resendTimer = setInterval(function () {
      seconds--;
      elResendCountdown.textContent = seconds;
      if (seconds <= 0) {
        clearInterval(resendTimer);
        btnResendOtp.disabled = false;
        btnResendOtp.innerHTML = 'Tekrar gönder';
      }
    }, 1000);
  }

  btnResendOtp.addEventListener('click', async function () {
    clearMsg(elMsgVerify);
    setLoading(btnResendOtp, true);
    try {
      await api('POST', '/api/points/identify', {
        phone_number: phoneNumber,
        shopify_customer_id: CUSTOMER_ID,
        shopify_email: CUSTOMER_EMAIL,
      });
      showMsg(elMsgVerify, 'Yeni doğrulama kodu gönderildi.', 'success');
      startResendCountdown();
      inputOtp.value = '';
      inputOtp.focus();
    } catch (e) {
      showMsg(elMsgVerify, e.message, 'error');
    } finally {
      setLoading(btnResendOtp, false);
    }
  });

  // Enter key support
  inputPhone.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') btnSendOtp.click();
  });
  inputOtp.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') btnVerifyOtp.click();
  });

  // ── Dashboard ───────────────────────────────────────────────────────────

  async function loadDashboard() {
    try {
      const data = await api('GET', '/api/points/balance');
      balance = data;
      voucherTypes = data.voucher_types || [];

      // Balance display
      elBalanceAmount.textContent = formatNumber(data.available_points);
      if (data.outstanding_voucher_points > 0) {
        elBalanceDetail.textContent =
          'Toplam: ' + formatNumber(data.nebim_total_points) + ' puan | ' +
          'Bekleyen kodlar: ' + formatNumber(data.outstanding_voucher_points) + ' puan';
      } else {
        elBalanceDetail.textContent = '';
      }

      // Voucher types
      renderVoucherTypes();

      // Amount input listener
      inputAmount.addEventListener('input', updateAmountPreview);

      // Load voucher history
      await loadVouchers();

    } catch (e) {
      showMsg(elMsgDashboard, 'Puan bilgisi yüklenemedi: ' + e.message, 'error');
    }
  }

  function renderVoucherTypes() {
    if (!voucherTypes.length) return;

    elVoucherTypes.innerHTML = voucherTypes.map(function (vt, i) {
      var cls = i === 0 ? ' is-selected' : '';
      return (
        '<div class="points__type-option' + cls + '" data-type="' + escapeHtml(vt.code) + '">' +
          '<div class="points__type-radio"></div>' +
          '<div class="points__type-info">' +
            '<div class="points__type-name">' + escapeHtml(vt.name) + '</div>' +
            '<div class="points__type-desc">' + escapeHtml(vt.description || '') + '</div>' +
          '</div>' +
          '<div class="points__type-multiplier">' +
            (vt.multiplier > 1 ? 'x' + vt.multiplier : '1:1') +
          '</div>' +
        '</div>'
      );
    }).join('');

    selectedType = voucherTypes[0];
    updateAmountLimits();

    // Click handlers
    elVoucherTypes.querySelectorAll('.points__type-option').forEach(function (el) {
      el.addEventListener('click', function () {
        elVoucherTypes.querySelectorAll('.points__type-option').forEach(function (o) {
          o.classList.remove('is-selected');
        });
        this.classList.add('is-selected');
        var code = this.dataset.type;
        selectedType = voucherTypes.find(function (vt) { return vt.code === code; });
        updateAmountLimits();
        updateAmountPreview();
      });
    });
  }

  function updateAmountLimits() {
    if (!selectedType) return;
    var max = selectedType.max_points;
    if (balance && max > balance.available_points) {
      max = balance.available_points;
    }
    inputAmount.min = selectedType.min_points;
    inputAmount.max = max;
    inputAmount.placeholder = 'Min ' + formatNumber(selectedType.min_points) +
      ' — Max ' + formatNumber(max);
  }

  function updateAmountPreview() {
    if (!selectedType) return;
    var pts = parseFloat(inputAmount.value) || 0;
    if (pts <= 0) {
      elAmountPreview.innerHTML = '';
      return;
    }
    var discount = pts * selectedType.multiplier;
    elAmountPreview.innerHTML =
      '<strong>' + formatNumber(pts) + '</strong> puan → ' +
      '<strong>' + formatNumber(discount) + ' TL</strong> indirim kodu' +
      (selectedType.multiplier > 1
        ? ' (x' + selectedType.multiplier + ' kampanya)'
        : '');
  }

  // ── Redeem ──────────────────────────────────────────────────────────────

  btnRedeem.addEventListener('click', async function () {
    clearMsg(elMsgDashboard);

    var pts = parseFloat(inputAmount.value) || 0;
    if (!selectedType) {
      showMsg(elMsgDashboard, 'Lütfen bir indirim türü seçin.', 'error');
      return;
    }
    if (pts < selectedType.min_points) {
      showMsg(elMsgDashboard, 'Minimum ' + formatNumber(selectedType.min_points) + ' puan gerekli.', 'error');
      return;
    }
    if (balance && pts > balance.available_points) {
      showMsg(elMsgDashboard, 'Yetersiz puan.', 'error');
      return;
    }

    setLoading(btnRedeem, true);
    try {
      var resp = await api('POST', '/api/points/redeem', {
        points: pts,
        voucher_type_code: selectedType.code,
      });

      // Show result
      elResultCode.textContent = resp.serial_number;
      var expiresDate = new Date(resp.expires_at);
      elResultMeta.innerHTML =
        formatNumber(resp.points_spent) + ' puan → ' +
        '<strong>' + formatNumber(resp.discount_amount) + ' TL</strong> indirim<br>' +
        'Geçerlilik: ' + expiresDate.toLocaleDateString('tr-TR');

      hide(elRedeemSection);
      show(elVoucherResult);

      // Refresh balance and vouchers
      inputAmount.value = '';
      elAmountPreview.innerHTML = '';
      await loadDashboard();

    } catch (e) {
      showMsg(elMsgDashboard, e.message, 'error');
    } finally {
      setLoading(btnRedeem, false);
    }
  });

  inputAmount.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') btnRedeem.click();
  });

  // Copy code
  btnCopyCode.addEventListener('click', function () {
    var code = elResultCode.textContent;
    navigator.clipboard.writeText(code).then(function () {
      btnCopyCode.title = 'Kopyalandı!';
      setTimeout(function () { btnCopyCode.title = 'Kopyala'; }, 2000);
    });
  });

  // New code
  btnNewCode.addEventListener('click', function () {
    hide(elVoucherResult);
    show(elRedeemSection);
    clearMsg(elMsgDashboard);
  });

  // ── Voucher history ─────────────────────────────────────────────────────

  async function loadVouchers() {
    try {
      var data = await api('GET', '/api/points/vouchers');
      var vouchers = data.vouchers || [];

      if (vouchers.length === 0) {
        show(elVoucherEmpty);
        return;
      }

      hide(elVoucherEmpty);

      // Keep the empty state element, clear others
      var existing = elVoucherList.querySelectorAll('.points__voucher-item');
      existing.forEach(function (el) { el.remove(); });

      vouchers.forEach(function (v) {
        var badgeClass = 'points__badge--' + v.status;
        var badgeText = {
          'active': 'Aktif',
          'used': 'Kullanıldı',
          'expired': 'Süresi Doldu',
          'refunded': 'İade Edildi',
          'cancelled': 'İptal',
        }[v.status] || v.status;

        var createdDate = new Date(v.created_at).toLocaleDateString('tr-TR');
        var detail = formatNumber(v.points_spent) + ' puan → ' +
          formatNumber(v.discount_amount) + ' TL';
        if (v.multiplier > 1) detail += ' (x' + v.multiplier + ')';
        if (v.shopify_order_name) detail += ' | Sipariş: ' + escapeHtml(v.shopify_order_name);

        var html =
          '<div class="points__voucher-item">' +
            '<div>' +
              '<div class="points__voucher-code">' + escapeHtml(v.serial_number) + '</div>' +
              '<div class="points__voucher-amounts">' + escapeHtml(detail) + '</div>' +
              '<div class="points__voucher-date">' + createdDate + '</div>' +
            '</div>' +
            '<span class="points__badge ' + badgeClass + '">' + badgeText + '</span>' +
          '</div>';

        elVoucherEmpty.insertAdjacentHTML('beforebegin', html);
      });

    } catch (e) {
      // Silently fail — not critical
      console.warn('Voucher history load failed:', e);
    }
  }

  // ── Bootstrap ───────────────────────────────────────────────────────────
  init();
})();
